import { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { ArrowLeft, Download, RefreshCcw } from "lucide-react";
import { apiAuth } from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import { StatusBadge, PIPELINE_STARTUPS } from "@/components/admin/StatusBadge";
import { StatusChangeModal } from "@/components/admin/StatusChangeModal";
import NotesPanel from "@/components/admin/NotesPanel";
import TagsPanel from "@/components/admin/TagsPanel";
import TimelineList from "@/components/admin/TimelineList";

const TABS = [
  ["overview", "Übersicht"],
  ["deck", "Pitch Deck"],
  ["ai", "AI-Analyse"],
  ["founders", "Founders"],
  ["notes", "Notes"],
  ["tags", "Tags"],
  ["timeline", "Timeline"],
  ["consent", "Consent"],
];

const Score = ({ label, value, accent = "bg-skyMist" }) => (
  <div className={`rounded-2xl p-4 ${accent}`}>
    <p className="text-xs text-cocoa/70 font-medium">{label}</p>
    <p className="font-serif text-3xl text-cocoa mt-1">{value ?? "—"}</p>
  </div>
);

export default function AdminStartupDetail() {
  const { id } = useParams();
  const { user } = useAuth();
  const [data, setData] = useState(null);
  const [tab, setTab] = useState("overview");
  const [statusOpen, setStatusOpen] = useState(false);
  const [reanalyzing, setReanalyzing] = useState(false);
  const [error, setError] = useState(null);

  const load = async () => {
    try {
      const { data } = await apiAuth.get(`/admin/startups/${id}`);
      setData(data);
    } catch (_e) { setError("Startup nicht gefunden."); }
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [id]);

  if (error) return <p className="text-sm text-coral">{error}</p>;
  if (!data) return <p className="text-sm text-warmGrey">Lade …</p>;

  const { startup, ai_analysis, decks, consents } = data;

  const downloadDeck = async (version) => {
    try {
      const { data } = await apiAuth.get(
        `/admin/startups/${id}/deck/sign${version ? `?version=${version}` : ""}`
      );
      // 5min-signed url is bound to admin id in the token. Open in new tab.
      window.open(data.url, "_blank", "noopener,noreferrer");
    } catch (_e) { /* ignore */ }
  };

  const reanalyze = async () => {
    setReanalyzing(true);
    try {
      await apiAuth.post(`/admin/startups/${id}/analyze`);
      await load();
    } catch (_e) { /* ignore */ }
    finally { setReanalyzing(false); }
  };

  return (
    <div data-testid="page-startup-detail" className="flex flex-col gap-6">
      <Link to="/admin/startups" className="text-xs text-warmGrey hover:text-raspberry inline-flex items-center gap-1">
        <ArrowLeft size={12} /> Zurück zur Liste
      </Link>

      <header className="bg-white border border-sandBeige rounded-3xl p-6 flex flex-col md:flex-row md:items-center gap-4 justify-between">
        <div>
          <h1 className="font-serif text-3xl text-cocoa">{startup.company?.name}</h1>
          <p className="text-sm text-warmGrey mt-1">{startup.company?.one_liner}</p>
          <div className="mt-3 flex flex-wrap items-center gap-3">
            <StatusBadge value={startup.status} />
            <span className="text-xs text-warmGrey">{startup.company?.country}</span>
            <span className="text-xs text-warmGrey">{startup.details?.stage}</span>
          </div>
        </div>
        <div className="flex flex-wrap gap-2">
          <button type="button" onClick={() => setStatusOpen(true)} className="wit-btn-secondary text-xs" data-testid="btn-change-status">
            Status ändern
          </button>
          {decks?.[0] && (
            <button type="button" onClick={() => downloadDeck()} className="wit-btn-secondary text-xs" data-testid="btn-download-deck">
              <Download size={13} className="inline mr-1" /> Deck (v{decks[0].version})
            </button>
          )}
          {decks?.[0] && (
            <button type="button" onClick={reanalyze} disabled={reanalyzing} className="wit-btn-primary text-xs disabled:opacity-60" data-testid="btn-reanalyze">
              <RefreshCcw size={13} className={`inline mr-1 ${reanalyzing ? "animate-spin" : ""}`} /> Re-Analyze
            </button>
          )}
        </div>
      </header>

      {/* Tabs */}
      <nav className="flex flex-wrap gap-2 -mb-1" data-testid="startup-tabs">
        {TABS.map(([key, label]) => (
          <button key={key} type="button" onClick={() => setTab(key)}
            className={`wit-pill text-xs ${tab === key ? "bg-raspberry text-white border-raspberry" : "bg-white border-sandBeige text-cocoa"}`}
            data-testid={`tab-${key}`}
          >
            {label}
          </button>
        ))}
      </nav>

      <section className="bg-white border border-sandBeige rounded-3xl p-6">
        {tab === "overview" && (
          <dl className="grid sm:grid-cols-2 gap-x-8 gap-y-3 text-sm" data-testid="tab-content-overview">
            <Row label="Company" value={startup.company?.name} />
            <Row label="Website" value={startup.company?.website} />
            <Row label="LinkedIn" value={startup.company?.linkedin} />
            <Row label="Founded" value={startup.company?.founded_year} />
            <Row label="HQ" value={`${startup.company?.city || ""} ${startup.company?.country || ""}`} />
            <Row label="Sektoren" value={(startup.company?.sectors || []).join(", ")} />
            <Row label="Stage" value={startup.details?.stage} />
            <Row label="Business Models" value={(startup.details?.business_models || []).join(", ")} />
            <Row label="Ask" value={startup.funding?.ask_amount ? `${startup.funding.ask_amount} ${startup.funding.ask_currency || "EUR"}` : "—"} />
            <Row label="Runway" value={startup.funding?.runway_months ? `${startup.funding.runway_months} Monate` : "—"} />
            <Row label="Submitted" value={startup.created_at ? new Date(startup.created_at).toLocaleString("de-DE") : "—"} />
            <Row label="Sprache" value={(startup.language || "").toUpperCase()} />
            <div className="sm:col-span-2">
              <dt className="text-xs text-warmGrey">Beschreibung</dt>
              <dd className="text-sm text-cocoa whitespace-pre-wrap mt-1">{startup.company?.description || "—"}</dd>
            </div>
          </dl>
        )}

        {tab === "deck" && (
          <div data-testid="tab-content-deck">
            {decks.length === 0 && <p className="text-sm text-warmGrey">Noch kein Pitch Deck.</p>}
            <ul className="divide-y divide-sandBeige/60">
              {decks.map((d) => (
                <li key={d.id} className="py-3.5 flex items-center justify-between gap-3" data-testid={`deck-row-${d.version}`}>
                  <div>
                    <p className="text-sm font-medium text-cocoa">{d.filename || `deck-v${d.version}.pdf`}</p>
                    <p className="text-xs text-warmGrey">v{d.version} · {Math.round((d.size || 0)/1024)} KB · {d.uploaded_at ? new Date(d.uploaded_at).toLocaleString("de-DE") : ""}</p>
                  </div>
                  <button type="button" onClick={() => downloadDeck(d.version)} className="wit-btn-secondary text-xs">
                    <Download size={12} className="inline mr-1" /> Download
                  </button>
                </li>
              ))}
            </ul>
          </div>
        )}

        {tab === "ai" && (
          <div data-testid="tab-content-ai">
            {!ai_analysis && (
              <div className="bg-lavenderFog/40 border border-lavenderFog rounded-3xl p-8 flex flex-col items-center text-center gap-5" data-testid="ai-empty-state">
                <div className="w-14 h-14 rounded-full bg-white border border-cardEdge flex items-center justify-center">
                  <RefreshCcw size={20} className={`text-raspberry ${reanalyzing ? "animate-spin" : ""}`} strokeWidth={1.6} />
                </div>
                <div>
                  <p className="font-serif text-2xl text-cocoa">Noch keine Voranalyse vorhanden</p>
                  <p className="text-sm text-warmGrey mt-2 max-w-md">
                    {decks?.[0]
                      ? "Pitch Deck ist hochgeladen. Starte die AI-Voranalyse, um Scores, Stärken und Red Flags strukturiert zu sehen."
                      : "Sobald ein Pitch Deck hochgeladen wurde, kannst du die Voranalyse hier starten."}
                  </p>
                </div>
                {decks?.[0] && (
                  <button
                    type="button"
                    onClick={reanalyze}
                    disabled={reanalyzing}
                    className="wit-btn-primary disabled:opacity-60"
                    data-testid="ai-empty-analyze"
                  >
                    {reanalyzing ? "Analyse läuft …" : (
                      <>
                        <RefreshCcw size={14} className="inline mr-1.5" />
                        Voranalyse jetzt starten
                      </>
                    )}
                  </button>
                )}
              </div>
            )}
            {ai_analysis && (
              <div className="flex flex-col gap-5">
                {ai_analysis.is_mocked && (
                  <div className="bg-butterCream border border-butterCream rounded-2xl px-4 py-2.5 text-sm text-cocoa">
                    <strong>MOCKED ANALYSIS</strong> — Live-Mode aktivieren via OPENAI_API_KEY + OPENAI_ANALYSIS_ENABLED=true.
                  </div>
                )}
                <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
                  <Score label="Overall Fit" value={ai_analysis.analysis?.overall_fit_score} accent="bg-sageMint" />
                  <Score label="Investor Readiness" value={ai_analysis.analysis?.investor_readiness_score} accent="bg-skyMist" />
                  <Score label="Team" value={ai_analysis.analysis?.team_score} accent="bg-lavenderFog" />
                  <Score label="Market" value={ai_analysis.analysis?.market_score} accent="bg-roseMilk" />
                  <Score label="Traction" value={ai_analysis.analysis?.traction_score} accent="bg-butterCream" />
                  <Score label="Differentiation" value={ai_analysis.analysis?.differentiation_score} accent="bg-sageMint" />
                  <Score label="Business Model" value={ai_analysis.analysis?.business_model_score} accent="bg-skyMist" />
                  <Score label="Risk Level" value={ai_analysis.analysis?.risk_level} accent="bg-coral/30" />
                </div>
                <ListBlock title="Strengths" items={ai_analysis.analysis?.strengths} accent="bg-sageMint/30" />
                <ListBlock title="Weaknesses" items={ai_analysis.analysis?.weaknesses} accent="bg-roseMilk/30" />
                <ListBlock title="Red Flags" items={ai_analysis.analysis?.red_flags} accent="bg-coral/20" />
                <ListBlock title="Missing Info" items={ai_analysis.analysis?.missing_info} accent="bg-butterCream/40" />
                <ListBlock title="Recommended Questions" items={ai_analysis.analysis?.recommended_questions} accent="bg-lavenderFog/40" />
                <div className="bg-porcelain rounded-2xl p-4">
                  <p className="wit-eyebrow text-warmGrey">Suggested Next Action</p>
                  <p className="text-sm text-cocoa mt-1">{ai_analysis.analysis?.suggested_next_action || "—"}</p>
                </div>
                <p className="text-xs text-warmGrey">
                  Model: <strong>{ai_analysis.model_id || "mock"}</strong> · Version: {ai_analysis.model_version || "v1"} · Tokens used: {ai_analysis.tokens_used ?? 0} · Confidence: {ai_analysis.analysis?.confidence_score ?? "—"} · {ai_analysis.created_at ? new Date(ai_analysis.created_at).toLocaleString("de-DE") : ""}
                </p>
              </div>
            )}
          </div>
        )}

        {tab === "founders" && (
          <div data-testid="tab-content-founders">
            {(!startup.founders || startup.founders.length === 0) && <p className="text-sm text-warmGrey">Keine Founder erfasst.</p>}
            <ul className="divide-y divide-sandBeige/60">
              {(startup.founders || []).map((f, i) => (
                <li key={i} className="py-4 flex justify-between gap-4">
                  <div>
                    <p className="text-sm font-medium text-cocoa">{f.first_name} {f.last_name} <span className="text-warmGrey font-normal text-xs">· {f.role}</span></p>
                    <p className="text-xs text-warmGrey">{f.email}</p>
                    {f.linkedin_url && <a href={f.linkedin_url} target="_blank" rel="noreferrer" className="text-xs text-raspberry">{f.linkedin_url}</a>}
                  </div>
                  <div className="flex flex-col gap-1 text-xs text-warmGrey items-end">
                    {f.technical && <span>Technical ✓</span>}
                    {f.female_led && <span>Female-led ✓</span>}
                    {f.diverse_team && <span>Diverse Team ✓</span>}
                  </div>
                </li>
              ))}
            </ul>
          </div>
        )}

        {tab === "notes" && <NotesPanel entityType="startup" entityId={id} currentUserId={user?.id} />}
        {tab === "tags" && <TagsPanel entityType="startup" entityId={id} initialTags={startup.tags || []} />}
        {tab === "timeline" && <TimelineList entityType="startup" entityId={id} />}
        {tab === "consent" && (
          <div data-testid="tab-content-consent">
            {(!consents || consents.length === 0) && <p className="text-sm text-warmGrey">Keine Consent-Records.</p>}
            <ul className="divide-y divide-sandBeige/60">
              {consents.map((c) => (
                <li key={c.id} className="py-3">
                  <p className="text-sm text-cocoa">{c.consent_text}</p>
                  <p className="text-xs text-warmGrey mt-1">{c.consent_type} · {c.consent_version} · {c.language?.toUpperCase()} · {c.granted_at ? new Date(c.granted_at).toLocaleString("de-DE") : ""}</p>
                </li>
              ))}
            </ul>
          </div>
        )}
      </section>

      <StatusChangeModal
        open={statusOpen} onClose={() => setStatusOpen(false)}
        onSaved={(s) => setData((d) => ({ ...d, startup: { ...d.startup, status: s } }))}
        entityType="startup" entityId={id}
        currentStatus={startup.status}
        options={PIPELINE_STARTUPS}
      />
    </div>
  );
}

const Row = ({ label, value }) => (
  <div className="contents">
    <dt className="text-xs text-warmGrey">{label}</dt>
    <dd className="text-sm text-cocoa">{value || "—"}</dd>
  </div>
);

const ListBlock = ({ title, items, accent }) => {
  if (!items || items.length === 0) return null;
  return (
    <div className={`rounded-2xl p-4 ${accent}`}>
      <p className="wit-eyebrow text-cocoa/80">{title}</p>
      <ul className="mt-2 list-disc list-inside text-sm text-cocoa space-y-1">
        {items.map((it, i) => <li key={i}>{it}</li>)}
      </ul>
    </div>
  );
};
